import axios from '@/utils/axios';
import { Booking, BookingStats } from '../types';
import {
  attachAssignmentWarnings,
  type WithAssignmentWarnings,
} from '../utils/assignmentWarnings';

export type BookingRecurrenceChangeCleanerPolicy = 'keep_if_available';

export type BookingRecurrenceChangePayload = {
  recurring_days: string;
  effective_from_date: string;
  effective_from_session_id?: number;
  interval_number?: 1 | 2;
  reason: string;
  cleaner_policy: BookingRecurrenceChangeCleanerPolicy;
  force?: boolean;
  apply_despite_protected_sessions?: boolean;
};

export type BookingRecurrenceChangePreviewItem = {
  id?: number;
  session_id?: number;
  booking_session_id?: number;
  date?: string;
  old_date?: string;
  new_date?: string;
  from_date?: string;
  to_date?: string;
  time?: string;
  message?: string;
  reason?: string;
  reasons?: string[];
  cleaner_id?: number;
  cleaner_name?: string;
  status?: string;
  action?: 'kept' | 'removed' | 'skipped';
  [key: string]: unknown;
};

export type BookingRecurrenceChangePreviewResponse = {
  booking_id?: number;
  current_recurring_days?: string;
  target_recurring_days?: string;
  current_interval_number?: number;
  target_interval_number?: 1 | 2;
  effective_from_date?: string;
  effective_from_session_id?: number;
  horizon_end?: string;
  preview?: boolean;
  applied?: boolean;
  unchanged_sessions?: BookingRecurrenceChangePreviewItem[];
  reused_sessions?: BookingRecurrenceChangePreviewItem[];
  created_sessions?: BookingRecurrenceChangePreviewItem[];
  removed_session_ids?: number[];
  protected_sessions?: BookingRecurrenceChangePreviewItem[];
  cleaner_results?: BookingRecurrenceChangePreviewItem[];
  moved?: BookingRecurrenceChangePreviewItem[];
  created?: BookingRecurrenceChangePreviewItem[];
  deleted?: BookingRecurrenceChangePreviewItem[];
  unassigned?: BookingRecurrenceChangePreviewItem[];
  skipped?: BookingRecurrenceChangePreviewItem[];
  protected?: BookingRecurrenceChangePreviewItem[];
  conflicts?: BookingRecurrenceChangePreviewItem[];
  blocked?: boolean;
  can_apply?: boolean;
  message?: string;
  data?: unknown;
};

// BOOKINGS
// export async function getBookings(): Promise<Booking[]> {
//   const {
//     data: { bookings },
//   } = await axios.get(`/bookings`);
//   return bookings;
// }

interface GetBookingsParams {
  page: number;
  limit: number;
  search?: string;
  serviceId?: number;
  serviceTypeId?: number;
  startDate?: string;
  endDate?: string;
  status?: string; // e.g. "All", "Pending", "Confirmed"
}

interface GetBookingsResponse {
  data: Booking[];
  total: number;
  statusCounts: Record<string, number>;
}

const isExactNumericSearch = (value?: string) =>
  typeof value === 'string' && /^\d+$/.test(value.trim());

const getBookingStatusKey = (booking: Booking) =>
  (
    booking.booking_status?.value ??
    booking.booking_status?.display ??
    ''
  ).toLowerCase();

const buildSingleBookingStatusCounts = (booking: Booking) => {
  const statusKey = getBookingStatusKey(booking);

  return {
    all: 1,
    ...(statusKey ? { [statusKey]: 1 } : {}),
  };
};

const bookingMatchesActiveFilters = (
  booking: Booking,
  {
    serviceId,
    serviceTypeId,
    startDate,
    endDate,
    status,
  }: Pick<
    GetBookingsParams,
    'serviceId' | 'serviceTypeId' | 'startDate' | 'endDate' | 'status'
  >
) => {
  if (serviceTypeId && serviceTypeId !== 0) {
    if (booking.service_variant_id !== serviceTypeId) return false;
  }

  if (serviceId && serviceId !== 0) {
    const bookingServiceId = booking.service_variant?.service?.id;
    if (bookingServiceId !== serviceId) return false;
  }

  if (status && status !== 'All') {
    const expectedStatus = status.toLowerCase();
    const bookingStatus = getBookingStatusKey(booking);
    const bookingStatusDisplay = booking.booking_status?.display?.toLowerCase();

    if (
      bookingStatus !== expectedStatus &&
      bookingStatusDisplay !== expectedStatus
    ) {
      return false;
    }
  }

  if (startDate || endDate) {
    const bookingCreatedAt = booking.created_at
      ? new Date(booking.created_at)
      : null;

    if (!bookingCreatedAt || Number.isNaN(bookingCreatedAt.getTime())) {
      return false;
    }

    if (startDate && bookingCreatedAt < new Date(startDate)) return false;
    if (endDate && bookingCreatedAt > new Date(endDate)) return false;
  }

  return true;
};

const getBookingByExactNumericSearch = async (
  params: GetBookingsParams
): Promise<GetBookingsResponse | null> => {
  if (!isExactNumericSearch(params.search) || params.page !== 1) return null;

  try {
    const booking = await getSingleBooking(Number(params.search?.trim()));

    if (!bookingMatchesActiveFilters(booking, params)) {
      return { data: [], total: 0, statusCounts: {} };
    }

    return {
      data: [booking],
      total: 1,
      statusCounts: buildSingleBookingStatusCounts(booking),
    };
  } catch (_error) {
    return { data: [], total: 0, statusCounts: {} };
  }
};

export async function getBookings({
  page,
  limit,
  search,
  serviceId,
  serviceTypeId,
  startDate,
  endDate,
  status,
}: GetBookingsParams): Promise<GetBookingsResponse> {
  const getBookingsParams = {
    page,
    limit,
    search,
    serviceId,
    serviceTypeId,
    startDate,
    endDate,
    status,
  };
  const params: Record<string, any> = {
    page,
    limit,
  };

  if (search) params.search = search;
  if (serviceId && serviceId !== 0) params.serviceId = serviceId;
  if (serviceTypeId && serviceTypeId !== 0)
    params.serviceTypeId = serviceTypeId;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (status && status !== 'All') params.status = status;

  // Adjust the endpoint and parameter names to match your backend
  const response = await axios.get('/bookings', { params });

  const {
    data: { bookings, total, statusCounts },
  } = response;

  if (bookings.length === 0 && total === 0) {
    const bookingIdSearchResult = await getBookingByExactNumericSearch(
      getBookingsParams
    );

    if (bookingIdSearchResult) return bookingIdSearchResult;
  }

  // console.log(bookings);
  return { data: bookings, total, statusCounts: statusCounts };
}

export async function getFilteredBookings(
  startDate: string,
  endDate?: string
): Promise<Booking[]> {
  const queryParams = `?startDate=${startDate}`;
  endDate && queryParams.concat(`&endDate=${endDate}`);
  const {
    data: { bookings },
  } = await axios.get(`/bookings/filter${queryParams}`);
  return bookings;
}

export async function getSingleBooking(id: number): Promise<Booking> {
  const {
    data: { data },
  } = await axios.get(`/bookings/${id}`);
  return data;
}

export async function createBooking(booking: Booking): Promise<any> {
  const { data } = await axios.post(`/bookings`, booking);
  return data;
}

export async function updateBooking(
  bookingId: number,
  // TODO: check the type Partial<Booking>
  updatedBooking: any
): Promise<WithAssignmentWarnings<Booking>> {
  const response = await axios.put(`/bookings/${bookingId}`, updatedBooking);
  const payload = response?.data;
  return attachAssignmentWarnings(payload?.data?.[0], payload?.warnings);
}

export async function previewBookingRecurrenceChange(
  bookingId: number,
  payload: BookingRecurrenceChangePayload
): Promise<BookingRecurrenceChangePreviewResponse> {
  const response = await axios.post(
    `/bookings/${bookingId}/recurrence-change/preview`,
    payload
  );

  return response?.data?.data ?? response?.data;
}

export async function applyBookingRecurrenceChange(
  bookingId: number,
  payload: BookingRecurrenceChangePayload
): Promise<BookingRecurrenceChangePreviewResponse> {
  const response = await axios.post(
    `/bookings/${bookingId}/recurrence-change`,
    payload
  );

  return response?.data?.data ?? response?.data;
}

export async function updateBookingPaymentMethod(
  bookingId: number,
  walletId: number
): Promise<any> {
  const {
    data: { data },
  } = await axios.put(`/bookings/${bookingId}/payment-method`, {
    walletId,
  });
  return data;
}

export async function sendBookingPaymentMethodRequest(
  bookingId: number
): Promise<any> {
  const {
    data: { data },
  } = await axios.post(`/bookings/${bookingId}/payment-method-request`, {
    channel: 'email',
  });
  return data;
}

export async function applyCouponToBooking(bookingId: number, code: string) {
  const {
    data: { data },
  } = await axios.put(`/bookings/${bookingId}/coupon`, { code });
  return data;
}

export async function removeCouponFromBooking(bookingId: number) {
  const {
    data: { data },
  } = await axios.delete(`/bookings/${bookingId}/coupon`);
  return data;
}

export async function deleteBooking(bookingId: number) {
  const { data } = await axios.delete(`/bookings/${bookingId}`);
  return data;
}

// =================================================================
// BOOKING STATS
export async function getBookingStats(): Promise<BookingStats> {
  const {
    data: { data },
  } = await axios.get('/stats/bookings');
  return data;
}
